import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import config from '../config';

const DOG_BREEDS = [
  'Affenpinscher',
  'Afghan Hound',
  'Airedale Terrier',
  'Akita',
  'Alaskan Malamute',
  'American Bulldog',
  'American Eskimo Dog',
  'American Foxhound',
  'American Pit Bull Terrier',
  'American Staffordshire Terrier',
  'Australian Cattle Dog',
  'Australian Shepherd',
  'Australian Terrier',
  'Basenji',
  'Basset Hound',
  'Beagle',
  'Bearded Collie',
  'Bedlington Terrier',
  'Belgian Malinois',
  'Bernese Mountain Dog',
  'Bichon Frise',
  'Bloodhound',
  'Border Collie',
  'Border Terrier',
  'Borzoi',
  'Boston Terrier',
  'Bouvier des Flandres',
  'Boxer',
  'Boykin Spaniel',
  'Briard',
  'Brittany',
  'Brussels Griffon',
  'Bull Terrier',
  'Bulldog',
  'Bullmastiff',
  'Cairn Terrier',
  'Cane Corso',
  'Cardigan Welsh Corgi',
  'Cavalier King Charles Spaniel',
  'Chesapeake Bay Retriever',
  'Chihuahua',
  'Chinese Crested',
  'Chinese Shar-Pei',
  'Chow Chow',
  'Clumber Spaniel',
  'Cocker Spaniel',
  'Collie',
  'Coton de Tulear',
  'Curly-Coated Retriever',
  'Dachshund',
  'Dalmatian',
  'Doberman Pinscher',
  'Dogo Argentino',
  'Dogue de Bordeaux',
  'English Cocker Spaniel',
  'English Setter',
  'English Springer Spaniel',
  'English Toy Spaniel',
  'Field Spaniel',
  'Finnish Spitz',
  'Flat-Coated Retriever',
  'French Bulldog',
  'German Pinscher',
  'German Shepherd',
  'German Shorthaired Pointer',
  'German Wirehaired Pointer',
  'Giant Schnauzer',
  'Golden Retriever',
  'Gordon Setter',
  'Great Dane',
  'Great Pyrenees',
  'Greater Swiss Mountain Dog',
  'Greyhound',
  'Harrier',
  'Havanese',
  'Ibizan Hound',
  'Irish Setter',
  'Irish Terrier',
  'Irish Water Spaniel',
  'Irish Wolfhound',
  'Italian Greyhound',
  'Jack Russell Terrier',
  'Japanese Chin',
  'Keeshond',
  'Kerry Blue Terrier',
  'Komondor',
  'Kuvasz',
  'Labrador Retriever',
  'Lakeland Terrier',
  'Leonberger',
  'Lhasa Apso',
  'Maltese',
  'Manchester Terrier',
  'Mastiff',
  'Miniature Pinscher',
  'Miniature Schnauzer',
  'Neapolitan Mastiff',
  'Newfoundland',
  'Norfolk Terrier',
  'Norwegian Elkhound',
  'Norwich Terrier',
  'Nova Scotia Duck Tolling Retriever',
  'Old English Sheepdog',
  'Otterhound',
  'Papillon',
  'Pekingese',
  'Pembroke Welsh Corgi',
  'Pharaoh Hound',
  'Pointer',
  'Pomeranian',
  'Poodle',
  'Portuguese Water Dog',
  'Pug',
  'Puli',
  'Rhodesian Ridgeback',
  'Rottweiler',
  'Saint Bernard',
  'Saluki',
  'Samoyed',
  'Schipperke',
  'Scottish Deerhound',
  'Scottish Terrier',
  'Sealyham Terrier',
  'Shetland Sheepdog',
  'Shiba Inu',
  'Shih Tzu',
  'Siberian Husky',
  'Silky Terrier',
  'Skye Terrier',
  'Soft Coated Wheaten Terrier',
  'Staffordshire Bull Terrier',
  'Standard Schnauzer',
  'Sussex Spaniel',
  'Tibetan Mastiff',
  'Tibetan Spaniel',
  'Tibetan Terrier',
  'Vizsla',
  'Weimaraner',
  'Welsh Springer Spaniel',
  'Welsh Terrier',
  'West Highland White Terrier',
  'Whippet',
  'Wire Fox Terrier',
  'Wirehaired Pointing Griffon',
  'Xoloitzcuintli',
  'Yorkshire Terrier',
];

const SCORE_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'neutral', label: 'Neutral' },
];

const LORA_OPTIONS = [
  { value: 'Illustrator v1', label: 'Illustrator v1' },
  { value: 'Look to the Sky', label: 'Look to the Sky' },
];

const PuppyGenerator = ({ testMode = false }) => {
  const host = config.backendUrl;

  const [breed, setBreed] = useState('');
  const [lora, setLora] = useState(LORA_OPTIONS[0].value);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Score card inputs
  const [styleCorrect, setStyleCorrect] = useState('');
  const [dogAccurate, setDogAccurate] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saved, setSaved] = useState(false);

  // Generation history table
  const [historyPage, setHistoryPage] = useState(0);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  const handleScoreSave = async (e) => {
    e.preventDefault();

    if (!result || !result.id) {
      setSaveError('This generation has no id to score.');
      return;
    }
    if (!styleCorrect || !dogAccurate) {
      setSaveError('Please answer both questions before saving.');
      return;
    }

    setSaving(true);
    setSaveError(null);
    setSaved(false);

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) throw new Error('Not authenticated.');

      const response = await fetch(`${host}/api/admin/test-dog-generation/score`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: result.id,
          style: styleCorrect,
          accuracy: dogAccurate,
        }),
      });

      if (!response.ok) {
        throw new Error(`Request failed (HTTP ${response.status})`);
      }

      setSaved(true);
    } catch (err) {
      console.error('Saving score failed:', err);
      setSaveError(err.message || 'Something went wrong saving the score.');
    } finally {
      setSaving(false);
    }
  };

  const renderScoreOptions = (name, value, setValue) => (
    <div style={{ display: 'flex', gap: 'var(--spacing-lg)', flexWrap: 'wrap' }}>
      {SCORE_OPTIONS.map((opt) => (
        <label
          key={opt.value}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-xs)',
            fontSize: 'var(--font-size-sm)',
            cursor: 'pointer',
          }}
        >
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={(e) => {
              setValue(e.target.value);
              setSaved(false);
              setSaveError(null);
            }}
          />
          {opt.label}
        </label>
      ))}
    </div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!breed) {
      setError('Please select a breed first.');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setStyleCorrect('');
    setDogAccurate('');
    setSaveError(null);
    setSaved(false);

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) throw new Error('Not authenticated.');

      const response = await fetch(`${host}/api/admin/test-dog-generation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ breed, lora }),
      });

      if (!response.ok) {
        throw new Error(`Request failed (HTTP ${response.status})`);
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      console.error('Dog generation failed:', err);
      setError(err.message || 'Something went wrong generating the image.');
    } finally {
      setLoading(false);
    }
  };

  const displayBreed = (result && result.breed) || breed;

  const handleDownload = async () => {
    if (!result || !result.image_url) return;
    const filename = `${(displayBreed || 'puppy').toLowerCase().replace(/\s+/g, '-')}.png`;
    try {
      const res = await fetch(result.image_url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      // Cross-origin or network issue — fall back to opening the image in a new tab.
      console.error('Direct download failed, opening in new tab instead:', err);
      window.open(result.image_url, '_blank', 'noopener,noreferrer');
    }
  };

  const googleImagesUrl = `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(displayBreed + ' puppy')}`;

  const loadHistory = useCallback(
    async (page) => {
      setHistoryLoading(true);
      setHistoryError(null);
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        if (!token) throw new Error('Not authenticated.');

        const response = await fetch(`${host}/api/admin/test-dog-generations?page=${page}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error(`Request failed (HTTP ${response.status})`);
        setHistory(await response.json());
      } catch (err) {
        console.error('Failed to load generations:', err);
        setHistoryError(err.message || 'Failed to load generations.');
      } finally {
        setHistoryLoading(false);
      }
    },
    [host],
  );

  // Load the requested page on mount and whenever the page changes.
  useEffect(() => {
    loadHistory(historyPage);
  }, [historyPage, loadHistory]);

  // Page 0 is the most recent, so older generations live on higher page numbers.
  const goOlder = () => setHistoryPage((p) => p + 1);
  const goNewer = () => setHistoryPage((p) => Math.max(0, p - 1));

  const formatScore = (v) => (v === null || v === undefined || v === '' ? '—' : v);

  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    const ms = ts < 1e12 ? ts * 1000 : ts; // handle seconds vs. milliseconds
    return new Date(ms).toLocaleString();
  };

  return (
    <div className="main-content">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-header-title">
            Puppy Generator
            {testMode && <span style={{ color: 'var(--warning-color)' }}> (Test Mode)</span>}
          </h1>
        </div>
        <div className="page-header-actions">
          <Link to={testMode ? '/test' : '/'} className="btn btn-secondary">
            ← Back Home
          </Link>
        </div>
      </div>

      {/* Generator Form */}
      <div className="card" style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <div className="card-header">
          <h3 className="card-title">Generate a Puppy</h3>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label" htmlFor="breed-select">
                Dog breed
              </label>
              <select
                id="breed-select"
                className="form-select"
                value={breed}
                onChange={(e) => setBreed(e.target.value)}
                disabled={loading}
              >
                <option value="">Select a breed…</option>
                {DOG_BREEDS.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="lora-select">
                LoRA
              </label>
              <select
                id="lora-select"
                className="form-select"
                value={lora}
                onChange={(e) => setLora(e.target.value)}
                disabled={loading}
              >
                {LORA_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Generating…' : 'Generate'}
            </button>

            {error && (
              <p className="form-error" style={{ marginTop: 'var(--spacing-md)' }}>
                {error}
              </p>
            )}
          </form>
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="card" style={{ marginBottom: 'var(--spacing-2xl)' }}>
          <div className="card-body" style={{ textAlign: 'center', padding: 'var(--spacing-3xl)' }}>
            <div className="spinner" style={{ margin: '0 auto' }}></div>
            <p style={{ marginTop: 'var(--spacing-md)', color: 'var(--text-secondary)' }}>
              Generating a {breed}…
            </p>
          </div>
        </div>
      )}

      {/* Result + Score */}
      {result && !loading && (
        <div
          style={{
            display: 'flex',
            gap: 'var(--spacing-2xl)',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            marginBottom: 'var(--spacing-2xl)',
          }}
        >
          {/* Result card */}
          <div className="card" style={{ flex: '1 1 520px', margin: 0 }}>
            <div className="card-header">
              <h3 className="card-title">Generated {displayBreed}</h3>
            </div>
            <div className="card-body">
              {result.image_url ? (
                <img
                  src={result.image_url}
                  alt={displayBreed}
                  style={{
                    display: 'block',
                    width: '100%',
                    maxWidth: '512px',
                    height: 'auto',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-medium)',
                  }}
                />
              ) : (
                <p style={{ color: 'var(--text-secondary)' }}>No image was returned.</p>
              )}

              {result.prompt && (
                <p
                  style={{
                    marginTop: 'var(--spacing-md)',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text-secondary)',
                  }}
                >
                  <strong>Prompt:</strong> {result.prompt}
                </p>
              )}

              <div
                style={{
                  display: 'flex',
                  gap: 'var(--spacing-md)',
                  flexWrap: 'wrap',
                  marginTop: 'var(--spacing-lg)',
                }}
              >
                {result.image_url && (
                  <button type="button" className="btn btn-primary" onClick={handleDownload}>
                    ⬇ Download image
                  </button>
                )}
                <a
                  className="btn btn-secondary"
                  href={googleImagesUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  🔎 View real {displayBreed} puppy photos
                </a>
              </div>
            </div>
          </div>

          {/* Score card */}
          <div className="card" style={{ flex: '1 1 300px', margin: 0 }}>
            <div className="card-header">
              <h3 className="card-title">Score</h3>
            </div>
            <div className="card-body">
              <form onSubmit={handleScoreSave}>
                <div className="form-group">
                  <div className="form-label">Is the style correct?</div>
                  {renderScoreOptions('style-correct', styleCorrect, setStyleCorrect)}
                </div>

                <div className="form-group">
                  <div className="form-label">Is the dog accurate?</div>
                  {renderScoreOptions('dog-accurate', dogAccurate, setDogAccurate)}
                </div>

                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>

                {saveError && (
                  <p className="form-error" style={{ marginTop: 'var(--spacing-md)' }}>
                    {saveError}
                  </p>
                )}
                {saved && !saveError && (
                  <p
                    style={{
                      marginTop: 'var(--spacing-md)',
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--success-color)',
                    }}
                  >
                    ✓ Score saved.
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Past generations */}
      <div className="card" style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <div
          className="card-header"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <h3 className="card-title">Past Generations</h3>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => loadHistory(historyPage)}
            disabled={historyLoading}
          >
            🔄 Refresh
          </button>
        </div>

        <div className="card-body" style={{ padding: 0 }}>
          {historyError && (
            <p className="form-error" style={{ padding: 'var(--spacing-lg)', margin: 0 }}>
              {historyError}
            </p>
          )}

          {historyLoading && !history && (
            <div style={{ textAlign: 'center', padding: 'var(--spacing-3xl)' }}>
              <div className="spinner" style={{ margin: '0 auto' }}></div>
              <p style={{ marginTop: 'var(--spacing-md)', color: 'var(--text-secondary)' }}>
                Loading generations…
              </p>
            </div>
          )}

          {history && history.generations && history.generations.length > 0 && (
            <div className="table-container" style={{ margin: 0, borderRadius: 0 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Generated</th>
                    <th>Breed</th>
                    <th>LoRA</th>
                    <th>Style</th>
                    <th>Accuracy</th>
                    <th>Image</th>
                  </tr>
                </thead>
                <tbody>
                  {history.generations.map((g) => (
                    <tr key={g.id}>
                      <td
                        style={{
                          whiteSpace: 'nowrap',
                          color: 'var(--text-secondary)',
                          fontSize: 'var(--font-size-sm)',
                        }}
                      >
                        {formatTimestamp(g.timestamp)}
                      </td>
                      <td title={g.prompt || ''} style={{ fontWeight: 'var(--font-weight-medium)' }}>
                        {g.breed || '—'}
                      </td>
                      <td>{g.lora || 'Look to the Sky'}</td>
                      <td>{formatScore(g.style)}</td>
                      <td>{formatScore(g.accuracy)}</td>
                      <td>
                        {g.image_url ? (
                          <a href={g.image_url} target="_blank" rel="noopener noreferrer">
                            Open image
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {history && history.generations && history.generations.length === 0 && !historyLoading && (
            <p style={{ padding: 'var(--spacing-lg)', margin: 0, color: 'var(--text-secondary)' }}>
              No generations yet.
            </p>
          )}
        </div>

        {/* Pagination */}
        {history && history.total_pages > 0 && (
          <div
            className="card-footer"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--spacing-md)',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              className="btn btn-secondary"
              onClick={goNewer}
              disabled={historyLoading || historyPage === 0}
            >
              ← Newer
            </button>
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>
              Page {((history.page ?? historyPage) + 1)} of {history.total_pages} · {history.total} total
            </span>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={goOlder}
              disabled={historyLoading || !history.has_more}
            >
              View previous 10 →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PuppyGenerator;
